import { documentUrl } from "@/lib/images";
import { symbolForCurrency, CURRENCIES } from "@/lib/company";
import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtCur } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Building2, FileText, Trash2, Download } from "lucide-react";
import { useSort } from "@/lib/sort";
import { SortTh } from "@/components/SortTh";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import { isoDate, EXP_CATS, INC_CATS, LiveFxButton } from "@/pages/accounting/shared";

const PAGE_SIZES = [10, 20, 50, 100, 200, "all"];
function InvoicePager({ page, setPage, pageSize, setPageSize, total, testidPrefix }) {
  const ps = pageSize === "all" ? (total || 1) : pageSize;
  const pages = Math.max(1, Math.ceil((total || 0) / ps));
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-1 py-1 text-xs text-slate-500" data-testid={`${testidPrefix}-pager`}>
      <div className="flex items-center gap-2">
        <span>Sayfa başına</span>
        <select data-testid={`${testidPrefix}-pagesize`} value={String(pageSize)} onChange={(e) => { setPageSize(e.target.value === "all" ? "all" : Number(e.target.value)); setPage(1); }}
          className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs">
          {PAGE_SIZES.map((n) => <option key={String(n)} value={String(n)}>{n === "all" ? "Tümü" : n}</option>)}
        </select>
        <span>· {total} kayıt</span>
      </div>
      {pageSize !== "all" && total > pageSize && (
        <div className="flex items-center gap-1">
          <button data-testid={`${testidPrefix}-prev`} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-md border border-slate-300 px-2 py-1 disabled:opacity-40">Önceki</button>
          <span className="px-1">{page} / {pages}</span>
          <button data-testid={`${testidPrefix}-next`} disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} className="rounded-md border border-slate-300 px-2 py-1 disabled:opacity-40">Sonraki</button>
        </div>
      )}
    </div>
  );
}
async function exportInvoices(kind, status) {
  try {
    const res = await api.get(`/accounting/${kind}-invoices/export.xlsx`, { params: { status }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${kind === "incoming" ? "gelen" : "giden"}-faturalar.xlsx`;
    a.click(); URL.revokeObjectURL(url);
  } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Excel indirilemedi"); }
}

export function IncomingTab({ canWrite }) {
  const { locations: locs } = useActiveLocation();
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(0);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [status]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounting/incoming-invoices", { params: { status } })
      .then((r) => { setRows(r.data.items || []); setPending(r.data.pending || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/accounting/incoming-invoices/${id}/${action}`);
      const msg = { accept: "Fatura kabul edildi ve gidere işlendi", reject: "Fatura reddedildi", cancel: "Geri alındı — gider/borç etkisi kaldırıldı" };
      toast.success(msg[action] || "Güncellendi");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); } finally { setBusy(""); }
  };
  const del = async (id) => {
    if (!window.confirm("Fatura kaydı silinsin mi?")) return;
    try { await api.delete(`/accounting/incoming-invoices/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const STCLS = { pending: "bg-amber-100 text-amber-700", accepting: "bg-amber-100 text-amber-700", cancelling: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700", cancelled: "bg-slate-100 text-slate-500" };
  const STLBL = { pending: "Onay Bekliyor", accepting: "Tamamlanmamış Onay", cancelling: "Tamamlanmamış Geri Alma", accepted: "Kabul Edildi", rejected: "Reddedildi", cancelled: "Geri Alındı" };
  const FILTERS = [["pending", "Bekleyen İşlemler"], ["accepted", "Kabul Edilen"], ["rejected", "Reddedilen"], ["all", "Tümü"]];
  const gross = (r) => Number(r.amount || 0) * (1 + (Number(r.vat_rate || 0) / 100));

  return (
    <div className="space-y-3" data-testid="incoming-tab">
      <div className="rounded-md bg-white p-3 text-xs text-slate-500 ring-1 ring-inset ring-slate-200">
        <b className="text-slate-700">Gelen (alış) faturaları</b> — manuel girilir ya da İzibiz/EDM gelen kutusundan çekilir. Kabul edilince otomatik <b>gider</b> kaydı oluşur; tedarikçiyle eşleşiyorsa <b>tedarikçi borç bakiyesine</b> eklenir. Onaylanınca yönetici/asistan/muhasebe bilgilendirilir. <span className="text-slate-400">(EDM/İzibiz otomatik çekme, entegratör gelen-fatura WSDL'i girilince aktifleşir.)</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`incoming-filter-${k}`} onClick={() => setStatus(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${status === k ? "bg-slate-800 text-white" : "bg-white text-slate-500 ring-1 ring-inset ring-slate-200"}`}>
              {l}{k === "pending" && pending > 0 ? ` (${pending})` : ""}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="incoming-excel" variant="outline" onClick={() => exportInvoices("incoming", status)} className="border-slate-300 text-slate-600"><Download className="mr-1.5 h-4 w-4" /> Excel'e Aktar</Button>
          {canWrite && <Button data-testid="incoming-add-btn" onClick={() => setOpen(true)} className="bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Yeni Gelen Fatura</Button>}
        </div>
      </div>
      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <SortTh label="Tarih" k="invoice_date" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inc-sort-date" />
              <SortTh label="Fatura No" k="invoice_no" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inc-sort-no" />
              <SortTh label="Tedarikçi" k="supplier_name" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inc-sort-sup" />
              <SortTh label="Depo/Mağaza" k="location_name" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inc-sort-loc" />
              <SortTh label="Net" k="amount" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inc-sort-amount" />
              <th className="px-4 py-3 text-right">Toplam (KDV dahil)</th>
              <SortTh label="Durum" k="status" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inc-sort-status" />
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Gelen fatura yok. "Yeni Gelen Fatura" ile alış faturası girin; kabulde gider + tedarikçi bakiyesine işlenir.</td></tr>}
            {(() => {
              const _sorted = sortRows(rows, {
                invoice_date: (r) => r.invoice_date || "", invoice_no: (r) => r.invoice_no || "",
                supplier_name: (r) => r.supplier_name || "", location_name: (r) => r.location_name || "",
                amount: (r) => Number(r.amount) || 0, status: (r) => r.status || "",
              });
              const _ps = pageSize === "all" ? (_sorted.length || 1) : pageSize;
              return _sorted.slice((page - 1) * _ps, (page - 1) * _ps + _ps);
            })().map((r) => {
              const documentHref = documentUrl(r.document_url);
              return (
              <tr key={r.id} data-testid={`incoming-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500">{r.invoice_date}</td>
                <td className="px-4 py-3 font-medium text-slate-700">{r.invoice_no}
                  {documentHref && <a data-testid={`incoming-doc-${r.id}`} href={documentHref} target="_blank" rel="noreferrer" className="ml-1.5 inline-flex text-brand-green hover:text-emerald-700"><FileText className="h-3.5 w-3.5" /></a>}
                </td>
                <td className="px-4 py-3 text-slate-600">{r.supplier_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">{r.location_name ? <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Building2 className="h-3.5 w-3.5" />{r.location_name}</span> : <span className="text-xs text-slate-400">Genel</span>}</td>
                <td className="px-4 py-3 text-right tabular text-slate-600">{fmtCur(r.amount, r.currency)} <span className="text-[10px] text-slate-400">%{Math.round(r.vat_rate)}</span></td>
                <td className="px-4 py-3 text-right tabular font-bold text-red-600">{fmtCur(gross(r), r.currency)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STCLS[r.status]}`}>{STLBL[r.status]}</span></td>
                <td className="px-4 py-3 text-right">
                  {canWrite && r.status === "pending" && (
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`incoming-accept-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "accept")} className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">Kabul et → Gider</button>
                      <button data-testid={`incoming-reject-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "reject")} className="text-[11px] font-semibold text-slate-400 hover:text-red-600">Reddet</button>
                      <button data-testid={`incoming-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                  {r.status === "accepted" && (
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-[11px] text-emerald-600">✓ gidere işlendi</span>
                      {canWrite && <button data-testid={`incoming-cancel-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "cancel")} className="text-[11px] font-semibold text-slate-400 hover:text-red-600 disabled:opacity-50">Geri Al</button>}
                    </div>
                  )}
                  {canWrite && r.status === "accepting" && r.acceptance_version === 1 && <button data-testid={`incoming-resume-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "accept")} className="text-[11px] font-semibold text-emerald-700 disabled:opacity-50">Onayı tamamla</button>}
                  {canWrite && r.status === "cancelling" && r.cancellation_version === 1 && <button data-testid={`incoming-cancel-resume-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "cancel")} className="text-[11px] font-semibold text-red-600 disabled:opacity-50">Geri almayı tamamla</button>}
                  {canWrite && r.status === "cancelled" && <button data-testid={`incoming-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <InvoicePager page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={rows.length} testidPrefix="incoming" />
      {open && <IncomingInvoiceDialog locs={locs} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function IncomingInvoiceDialog({ locs, onClose, onSaved }) {
  const today = isoDate(new Date());
  const [form, setForm] = useState({ supplier_id: "", supplier_name: "", invoice_no: "", invoice_date: today, amount: "", vat_rate: "20", currency: "TRY", fx_rate: "", category: "Alış / Ticari Mal", location_id: "", description: "", document_url: "", add_to_supplier_balance: true });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supQ, setSupQ] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const documentHref = documentUrl(form.document_url);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/accounting/cari-options", { params: { type: "supplier", q: supQ || undefined } })
        .then((r) => setSuppliers(r.data || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [supQ]);
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("document_url", data.url); toast.success("Belge yüklendi");
    } catch (er) { toast.error(apiError(er.response?.data?.detail) || "Belge yüklenemedi"); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.invoice_no.trim()) { toast.error("Fatura no zorunlu"); return; }
    const amt = parseFloat(String(form.amount).replace(",", ".")) || 0;
    if (amt <= 0) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    if (!form.document_url) { toast.error("Fatura belgesi (dosya) yüklemesi zorunludur"); return; }
    setBusy(true);
    try {
      const sup = suppliers.find((s) => s.id === form.supplier_id);
      const payload = { ...form, amount: amt, vat_rate: parseFloat(form.vat_rate) || 0,
        fx_rate: form.currency !== "TRY" ? (parseFloat(String(form.fx_rate).replace(",", ".")) || null) : null,
        supplier_id: form.supplier_id || null, supplier_name: sup?.name || form.supplier_name || null,
        location_id: form.location_id || null };
      await api.post("/accounting/incoming-invoices", payload);
      toast.success("Gelen fatura kaydedildi (onay bekliyor)");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="incoming-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Yeni Gelen Fatura</DialogTitle>
          <DialogDescription>Alış faturası. Kaydedince onay bekler; kabulde gider + (tedarikçi seçiliyse) tedarikçi borcuna işlenir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Tedarikçi (opsiyonel — bakiyeye eklemek için seçin)</label>
            <Input data-testid="incoming-supplier-search" value={supQ} onChange={(e) => setSupQ(e.target.value)} placeholder="Tedarikçi ara..." className="mb-1 h-8" />
            <select data-testid="incoming-supplier" value={form.supplier_id} onChange={(e) => set("supplier_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option value="">Eşleşme yok / serbest</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {!form.supplier_id && <Input data-testid="incoming-supplier-name" value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} placeholder="Tedarikçi adı (serbest metin)" className="mt-1 h-8" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs text-slate-500">Fatura No *</label><Input data-testid="incoming-no" value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)} placeholder="GIB2026..." /></div>
            <div><label className="mb-1 block text-xs text-slate-500">Fatura Tarihi</label><DatePickerTR testid="incoming-date" value={form.invoice_date} onChange={(v) => set("invoice_date", v)} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="mb-1 block text-xs text-slate-500">Tutar (KDV hariç {symbolForCurrency(form.currency)})</label><Input data-testid="incoming-amount" inputMode="decimal" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" className="tabular" /></div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Para Birimi</label>
              <select data-testid="incoming-currency" value={form.currency} onChange={(e) => set("currency", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                {Object.keys(CURRENCIES).map((c) => <option key={c} value={c}>{symbolForCurrency(c)} {c}</option>)}
              </select>
              {form.currency !== "TRY" && (
                <div className="mt-1 flex items-center gap-1">
                  <Input data-testid="incoming-fx" inputMode="decimal" value={form.fx_rate} onChange={(e) => set("fx_rate", e.target.value)} placeholder="Kur (₺)" title="1 birim = ? TL — kâr/zarar defterine bu kurla TL çevrilir" className="h-8 tabular" />
                  <LiveFxButton currency={form.currency} onRate={(r) => set("fx_rate", String(r))} testid="incoming-fx-live" />
                </div>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">KDV %</label>
              <select data-testid="incoming-vat" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                {["0", "1", "10", "20"].map((v) => <option key={v} value={v}>{`%${v}`}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Kategori</label>
              <input data-testid="incoming-category" list="inc-cats" value={form.category} onChange={(e) => set("category", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" />
              <datalist id="inc-cats">{["Alış / Ticari Mal", ...EXP_CATS].map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Depo / Mağaza</label>
              <select data-testid="incoming-location" value={form.location_id} onChange={(e) => set("location_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                <option value="">Genel / Atanmamış</option>
                {(locs || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <div><label className="mb-1 block text-xs text-slate-500">Açıklama</label><Input data-testid="incoming-desc" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Opsiyonel" /></div>
          <div className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 ring-1 ring-inset ${form.document_url ? "bg-slate-50 ring-slate-200" : "bg-amber-50 ring-amber-300"}`}>
            <label className="text-xs text-slate-500">Belge (fatura PDF/görsel) <span className="font-semibold text-amber-600">*zorunlu</span></label>
            <div className="flex items-center gap-2">
              {documentHref && <a data-testid="incoming-doc-preview" href={documentHref} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center rounded px-2 text-xs font-semibold text-brand-green underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-brand-green">✓ yüklendi · Önizle</a>}
              <label className="cursor-pointer rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-white">
                {uploading ? "Yükleniyor..." : "Belge Yükle"}
                <input data-testid="incoming-doc-upload" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden" onChange={uploadDoc} />
              </label>
            </div>
          </div>
          {form.supplier_id && (
            <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="incoming-add-balance" type="checkbox" checked={form.add_to_supplier_balance} onChange={(e) => set("add_to_supplier_balance", e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Tedarikçi borç bakiyesine ekle</label>
          )}
          <Button data-testid="incoming-save" onClick={save} disabled={busy} className="w-full bg-brand-green text-white hover:bg-brand-green-600">{busy ? "Kaydediliyor..." : "Kaydet (onaya gönder)"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function OutgoingTab({ canWrite }) {
  const { locations: locs } = useActiveLocation();
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(0);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [status]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounting/outgoing-invoices", { params: { status } })
      .then((r) => { setRows(r.data.items || []); setPending(r.data.pending || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/accounting/outgoing-invoices/${id}/${action}`);
      const msg = { accept: "Fatura kabul edildi ve gelire işlendi", reject: "Fatura reddedildi", cancel: "Geri alındı — gelir/alacak etkisi kaldırıldı" };
      toast.success(msg[action] || "Güncellendi");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); } finally { setBusy(""); }
  };
  const del = async (id) => {
    if (!window.confirm("Fatura kaydı silinsin mi?")) return;
    try { await api.delete(`/accounting/outgoing-invoices/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const STCLS = { pending: "bg-amber-100 text-amber-700", accepting: "bg-amber-100 text-amber-700", cancelling: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700", cancelled: "bg-slate-100 text-slate-500" };
  const STLBL = { pending: "Onay Bekliyor", accepting: "Tamamlanmamış Onay", cancelling: "Tamamlanmamış Geri Alma", accepted: "Kabul Edildi", rejected: "Reddedildi", cancelled: "Geri Alındı" };
  const FILTERS = [["pending", "Bekleyen İşlemler"], ["accepted", "Kabul Edilen"], ["rejected", "Reddedilen"], ["all", "Tümü"]];
  const gross = (r) => Number(r.amount || 0) * (1 + (Number(r.vat_rate || 0) / 100));

  return (
    <div className="space-y-3" data-testid="outgoing-tab">
      <div className="rounded-md bg-white p-3 text-xs text-slate-500 ring-1 ring-inset ring-slate-200">
        <b className="text-slate-700">Giden (satış) faturaları</b> — manuel girilir ya da İzibiz/EDM ile kesilir. Kabul edilince otomatik <b>gelir</b> kaydı oluşur; müşteriyle eşleşiyorsa <b>müşteri alacak bakiyesine</b> eklenir. Onaylanınca yönetici/asistan/muhasebe bilgilendirilir.
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`outgoing-filter-${k}`} onClick={() => setStatus(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${status === k ? "bg-slate-800 text-white" : "bg-white text-slate-500 ring-1 ring-inset ring-slate-200"}`}>
              {l}{k === "pending" && pending > 0 ? ` (${pending})` : ""}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="outgoing-excel" variant="outline" onClick={() => exportInvoices("outgoing", status)} className="border-slate-300 text-slate-600"><Download className="mr-1.5 h-4 w-4" /> Excel'e Aktar</Button>
          {canWrite && <Button data-testid="outgoing-add-btn" onClick={() => setOpen(true)} className="bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Yeni Giden Fatura</Button>}
        </div>
      </div>
      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <SortTh label="Tarih" k="invoice_date" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="out-sort-date" />
              <SortTh label="Fatura No" k="invoice_no" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="out-sort-no" />
              <SortTh label="Müşteri" k="customer_name" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="out-sort-cust" />
              <SortTh label="Depo/Mağaza" k="location_name" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="out-sort-loc" />
              <SortTh label="Net" k="amount" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="out-sort-amount" />
              <th className="px-4 py-3 text-right">Toplam (KDV dahil)</th>
              <SortTh label="Durum" k="status" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="out-sort-status" />
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Giden fatura yok. "Yeni Giden Fatura" ile satış faturası girin; kabulde gelir + müşteri alacağına işlenir.</td></tr>}
            {(() => {
              const _sorted = sortRows(rows, {
                invoice_date: (r) => r.invoice_date || "", invoice_no: (r) => r.invoice_no || "",
                customer_name: (r) => r.customer_name || "", location_name: (r) => r.location_name || "",
                amount: (r) => Number(r.amount) || 0, status: (r) => r.status || "",
              });
              const _ps = pageSize === "all" ? (_sorted.length || 1) : pageSize;
              return _sorted.slice((page - 1) * _ps, (page - 1) * _ps + _ps);
            })().map((r) => {
              const documentHref = documentUrl(r.document_url);
              return (
              <tr key={r.id} data-testid={`outgoing-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500">{r.invoice_date}</td>
                <td className="px-4 py-3 font-medium text-slate-700">{r.invoice_no}
                  {documentHref && <a data-testid={`outgoing-doc-${r.id}`} href={documentHref} target="_blank" rel="noreferrer" className="ml-1.5 inline-flex text-brand-green hover:text-emerald-700"><FileText className="h-3.5 w-3.5" /></a>}
                </td>
                <td className="px-4 py-3 text-slate-600">{r.customer_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">{r.location_name ? <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Building2 className="h-3.5 w-3.5" />{r.location_name}</span> : <span className="text-xs text-slate-400">Genel</span>}</td>
                <td className="px-4 py-3 text-right tabular text-slate-600">{fmtCur(r.amount, r.currency)} <span className="text-[10px] text-slate-400">%{Math.round(r.vat_rate)}</span></td>
                <td className="px-4 py-3 text-right tabular font-bold text-emerald-600">{fmtCur(gross(r), r.currency)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STCLS[r.status]}`}>{STLBL[r.status]}</span></td>
                <td className="px-4 py-3 text-right">
                  {canWrite && r.status === "pending" && (
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`outgoing-accept-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "accept")} className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">Kabul et → Gelir</button>
                      <button data-testid={`outgoing-reject-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "reject")} className="text-[11px] font-semibold text-slate-400 hover:text-red-600">Reddet</button>
                      <button data-testid={`outgoing-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                  {r.status === "accepted" && (
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-[11px] text-emerald-600">✓ gelire işlendi</span>
                      {canWrite && <button data-testid={`outgoing-cancel-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "cancel")} className="text-[11px] font-semibold text-slate-400 hover:text-red-600 disabled:opacity-50">Geri Al</button>}
                    </div>
                  )}
                  {canWrite && r.status === "accepting" && r.acceptance_version === 1 && <button data-testid={`outgoing-resume-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "accept")} className="text-[11px] font-semibold text-emerald-700 disabled:opacity-50">Onayı tamamla</button>}
                  {canWrite && r.status === "cancelling" && r.cancellation_version === 1 && <button data-testid={`outgoing-cancel-resume-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "cancel")} className="text-[11px] font-semibold text-red-600 disabled:opacity-50">Geri almayı tamamla</button>}
                  {canWrite && r.status === "cancelled" && <button data-testid={`outgoing-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <InvoicePager page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={rows.length} testidPrefix="outgoing" />
      {open && <OutgoingInvoiceDialog locs={locs} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function OutgoingInvoiceDialog({ locs, onClose, onSaved }) {
  const today = isoDate(new Date());
  const [form, setForm] = useState({ customer_id: "", customer_name: "", invoice_no: "", invoice_date: today, amount: "", vat_rate: "20", currency: "TRY", fx_rate: "", category: "Satış / Ticari Mal", location_id: "", description: "", document_url: "", add_to_customer_balance: true });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [custQ, setCustQ] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const documentHref = documentUrl(form.document_url);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/accounting/cari-options", { params: { type: "customer", q: custQ || undefined } })
        .then((r) => setCustomers(r.data || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [custQ]);
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("document_url", data.url); toast.success("Belge yüklendi");
    } catch (er) { toast.error(apiError(er.response?.data?.detail) || "Belge yüklenemedi"); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.invoice_no.trim()) { toast.error("Fatura no zorunlu"); return; }
    const amt = parseFloat(String(form.amount).replace(",", ".")) || 0;
    if (amt <= 0) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    if (!form.document_url) { toast.error("Fatura belgesi (dosya) yüklemesi zorunludur"); return; }
    setBusy(true);
    try {
      const cust = customers.find((c) => c.id === form.customer_id);
      const payload = { ...form, amount: amt, vat_rate: parseFloat(form.vat_rate) || 0,
        fx_rate: form.currency !== "TRY" ? (parseFloat(String(form.fx_rate).replace(",", ".")) || null) : null,
        customer_id: form.customer_id || null, customer_name: cust?.name || form.customer_name || null,
        location_id: form.location_id || null };
      await api.post("/accounting/outgoing-invoices", payload);
      toast.success("Giden fatura kaydedildi (onay bekliyor)");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="outgoing-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Yeni Giden Fatura</DialogTitle>
          <DialogDescription>Satış faturası. Kaydedince onay bekler; kabulde gelir + (müşteri seçiliyse) müşteri alacağına işlenir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Müşteri (opsiyonel — alacağa eklemek için seçin)</label>
            <Input data-testid="outgoing-customer-search" value={custQ} onChange={(e) => setCustQ(e.target.value)} placeholder="Müşteri ara..." className="mb-1 h-8" />
            <select data-testid="outgoing-customer" value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option value="">Eşleşme yok / serbest</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {!form.customer_id && <Input data-testid="outgoing-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Müşteri adı (serbest metin)" className="mt-1 h-8" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs text-slate-500">Fatura No *</label><Input data-testid="outgoing-no" value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)} placeholder="GIB2026..." /></div>
            <div><label className="mb-1 block text-xs text-slate-500">Fatura Tarihi</label><DatePickerTR testid="outgoing-date" value={form.invoice_date} onChange={(v) => set("invoice_date", v)} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="mb-1 block text-xs text-slate-500">Tutar (KDV hariç {symbolForCurrency(form.currency)})</label><Input data-testid="outgoing-amount" inputMode="decimal" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" className="tabular" /></div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Para Birimi</label>
              <select data-testid="outgoing-currency" value={form.currency} onChange={(e) => set("currency", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                {Object.keys(CURRENCIES).map((c) => <option key={c} value={c}>{symbolForCurrency(c)} {c}</option>)}
              </select>
              {form.currency !== "TRY" && (
                <div className="mt-1 flex items-center gap-1">
                  <Input data-testid="outgoing-fx" inputMode="decimal" value={form.fx_rate} onChange={(e) => set("fx_rate", e.target.value)} placeholder="Kur (₺)" title="1 birim = ? TL — kâr/zarar defterine bu kurla TL çevrilir" className="h-8 tabular" />
                  <LiveFxButton currency={form.currency} onRate={(r) => set("fx_rate", String(r))} testid="outgoing-fx-live" />
                </div>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">KDV %</label>
              <select data-testid="outgoing-vat" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                {["0", "1", "10", "20"].map((v) => <option key={v} value={v}>{`%${v}`}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Kategori</label>
              <input data-testid="outgoing-category" list="out-cats" value={form.category} onChange={(e) => set("category", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" />
              <datalist id="out-cats">{["Satış / Ticari Mal", ...INC_CATS].map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Depo / Mağaza</label>
              <select data-testid="outgoing-location" value={form.location_id} onChange={(e) => set("location_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                <option value="">Genel / Atanmamış</option>
                {(locs || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <div><label className="mb-1 block text-xs text-slate-500">Açıklama</label><Input data-testid="outgoing-desc" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Opsiyonel" /></div>
          <div className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 ring-1 ring-inset ${form.document_url ? "bg-slate-50 ring-slate-200" : "bg-amber-50 ring-amber-300"}`}>
            <label className="text-xs text-slate-500">Belge (fatura PDF/görsel) <span className="font-semibold text-amber-600">*zorunlu</span></label>
            <div className="flex items-center gap-2">
              {documentHref && <a data-testid="outgoing-doc-preview" href={documentHref} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center rounded px-2 text-xs font-semibold text-brand-green underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-brand-green">✓ yüklendi · Önizle</a>}
              <label className="cursor-pointer rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-white">
                {uploading ? "Yükleniyor..." : "Belge Yükle"}
                <input data-testid="outgoing-doc-upload" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden" onChange={uploadDoc} />
              </label>
            </div>
          </div>
          {form.customer_id && (
            <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="outgoing-add-balance" type="checkbox" checked={form.add_to_customer_balance} onChange={(e) => set("add_to_customer_balance", e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Müşteri alacak bakiyesine ekle</label>
          )}
          <Button data-testid="outgoing-save" onClick={save} disabled={busy} className="w-full bg-brand-green text-white hover:bg-brand-green-600">{busy ? "Kaydediliyor..." : "Kaydet (onaya gönder)"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
